'use client';

import React, { useState, useEffect } from 'react';
import CategoryFilter from './CategoryFilter';
import PhotoGallery from './PhotoGallery';
import VideoGallery from './VideoGallery';
import SuccessStories from './SuccessStories';
import InstagramFeed from './InstagramFeed';
import PhotoSubmission from './PhotoSubmission';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/utils/supabase/client';

interface Category {
  id: string;
  label: string;
  icon: string;
  count: number;
}

interface Photo {
  id: string;
  src: string;
  alt: string;
  category: string;
  location: string;
  date: string;
  title: string;
  description: string;
}

interface Video {
  id: string;
  thumbnail: string;
  title: string;
  description: string;
  category: string;
  location: string;
  date: string;
  videoUrl: string;
  language: string;
}

interface Story {
  id: string;
  name: string;
  image: string;
  category: string;
  location: string;
  date: string;
  story: string;
  impact: string;
  language: string;
}

interface InstagramPost {
  id: string;
  caption: string;
  post_url: string;
  image?: string;
}

const GalleryInteractive = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [activeTab, setActiveTab] = useState<'photos' | 'videos' | 'stories'>('photos');
  const [activeCategory, setActiveCategory] = useState('all');

  const [photos, setPhotos] = useState<Photo[]>([]);
  const [videos, setVideos] = useState<Video[]>([]);
  const [stories, setStories] = useState<Story[]>([]);
  const [instagramPosts, setInstagramPosts] = useState<InstagramPost[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setIsHydrated(true);

    const fetchGalleryData = async () => {
      setIsLoading(true);
      try {
        const supabase = createClient();
        
        // Fetch operations
        const [photosRes, videosRes, storiesRes, instagramRes] = await Promise.all([
          supabase.from('galleryphoto').select('*').order('date', { ascending: false }),
          supabase.from('galleryvideo').select('*').order('date', { ascending: false }),
          supabase.from('gallerystory').select('*').order('date', { ascending: false }),
          supabase.from('instagrampost').select('*').order('created_at', { ascending: false })
        ]);

        if (photosRes.error) console.error('galleryphoto fetch error:', photosRes.error);
        if (videosRes.error) console.error('galleryvideo fetch error:', videosRes.error);
        if (storiesRes.error) console.error('gallerystory fetch error:', storiesRes.error);
        if (instagramRes.error) console.error('instagrampost fetch error:', instagramRes.error);

        if (photosRes.data) {
          setPhotos(photosRes.data.map((p: any) => ({
            id: p.id.toString(),
            src: p.image_url || '',
            alt: p.title || '',
            category: p.category || 'Health Services',
            location: p.location || 'Surat',
            date: p.date || new Date().toISOString().split('T')[0],
            title: p.title || '',
            description: p.description || ''
          })));
        }

        if (videosRes.data) {
          setVideos(videosRes.data.map((v: any) => ({
            id: v.id.toString(),
            thumbnail: v.thumbnail_url || '',
            title: v.title || '',
            description: v.description || '',
            category: v.category || 'Health Services',
            location: v.location || 'Surat',
            date: v.date || new Date().toISOString().split('T')[0],
            videoUrl: v.video_url || '',
            language: v.language || 'Gujarati'
          })));
        }

        if (storiesRes.data) {
          setStories(storiesRes.data.map((s: any) => ({
            id: s.id.toString(),
            name: s.name || '',
            image: s.image || '',
            category: s.category || 'Health Services',
            location: s.location || 'Surat',
            date: s.date || new Date().toISOString().split('T')[0],
            story: s.story || '',
            impact: s.impact || '',
            language: s.language || 'Gujarati'
          })));
        }

        if (instagramRes.data) {
          setInstagramPosts(instagramRes.data.map((i: any) => ({
            id: i.id.toString(),
            post_url: i.post_url || '',
            caption: i.caption || '',
            image: i.image || '',
          })));
        }

      } catch (error) {
        console.error('Error fetching gallery data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchGalleryData();
  }, []);

  const categories: Category[] = [
    { id: 'all', label: 'All', icon: 'Squares2X2Icon', count: 0 },
    { id: 'health', label: 'Health Services', icon: 'HeartIcon', count: 0 },
    { id: 'community', label: 'Community Support', icon: 'UserGroupIcon', count: 0 },
    { id: 'events', label: 'Events', icon: 'CalendarIcon', count: 0 },
    { id: 'volunteers', label: 'Volunteers', icon: 'HandRaisedIcon', count: 0 },
    { id: 'facilities', label: 'Facilities', icon: 'BuildingOfficeIcon', count: 0 },
  ];


  const filteredPhotos = activeCategory === 'all' ?
  photos :
  photos.filter((photo) => {
    const categoryMap: {[key: string]: string;} = {
      'health': 'Health Services',
      'community': 'Community Support',
      'events': 'Events',
      'volunteers': 'Volunteers',
      'facilities': 'Facilities'
    };
    return photo.category === categoryMap[activeCategory];
  });

  const filteredVideos = activeCategory === 'all' ?
  videos :
  videos.filter((video) => {
    const categoryMap: {[key: string]: string;} = {
      'health': 'Health Services',
      'community': 'Community Support',
      'events': 'Events',
      'volunteers': 'Volunteers',
      'facilities': 'Facilities'
    };
    return video.category === categoryMap[activeCategory];
  });

  const filteredStories = activeCategory === 'all' ?
  stories :
  stories.filter((story) => {
    const categoryMap: {[key: string]: string;} = {
      'health': 'Health Services',
      'community': 'Community Support',
      'events': 'Events',
      'volunteers': 'Volunteers',
      'facilities': 'Facilities'
    };
    return story.category === categoryMap[activeCategory];
  });

  const handlePhotoSubmit = (data: FormData) => {
    console.log('Photo submission:', data);
  };

  if (!isHydrated || isLoading) {
    return (
      <div className="container mx-auto px-4 py-12">
        <div className="animate-pulse space-y-8">
          <div className="h-12 bg-muted rounded w-1/3 mx-auto" />
          <div className="grid grid-cols-3 gap-4">
            {[1, 2, 3, 4, 5, 6].map((i) =>
            <div key={i} className="aspect-square bg-muted rounded-lg" />
            )}
          </div>
        </div>
      </div>);

  }

  return (
    <div className="container mx-auto px-4 py-12">
      {/* Tab Navigation */}
      <div className="flex flex-wrap items-center justify-center gap-2 mb-6 sm:mb-8">
        <button
          onClick={() => setActiveTab('photos')}
          className={`flex items-center space-x-2 px-4 sm:px-6 py-2 sm:py-3 rounded-lg font-heading font-semibold transition-all duration-300 text-sm sm:text-base ${
          activeTab === 'photos' ? 'bg-primary text-primary-foreground shadow-cta' : 'bg-muted text-foreground hover:bg-muted/80'}`
          }>
          <Icon name="PhotoIcon" size={18} />
          <span>Photos</span>
        </button>
        <button
          onClick={() => setActiveTab('videos')}
          className={`flex items-center space-x-2 px-4 sm:px-6 py-2 sm:py-3 rounded-lg font-heading font-semibold transition-all duration-300 text-sm sm:text-base ${
          activeTab === 'videos' ? 'bg-primary text-primary-foreground shadow-cta' : 'bg-muted text-foreground hover:bg-muted/80'}`
          }>
          <Icon name="VideoCameraIcon" size={18} />
          <span>Videos</span>
        </button>
        <button
          onClick={() => setActiveTab('stories')}
          className={`flex items-center space-x-2 px-4 sm:px-6 py-2 sm:py-3 rounded-lg font-heading font-semibold transition-all duration-300 text-sm sm:text-base ${
          activeTab === 'stories' ? 'bg-primary text-primary-foreground shadow-cta' : 'bg-muted text-foreground hover:bg-muted/80'}`
          }>
          <Icon name="BookOpenIcon" size={18} />
          <span>Stories</span>
        </button>
      </div>

      {/* Category Filter */}
      <CategoryFilter
        categories={categories}
        activeCategory={activeCategory}
        onCategoryChange={setActiveCategory} />


      {/* Content Based on Active Tab */}
      {activeTab === 'photos' &&
      <div className="space-y-12">
          <PhotoGallery photos={filteredPhotos} />
        </div>
      }

      {activeTab === 'videos' &&
      <div className="space-y-12">
          <VideoGallery videos={filteredVideos} />
        </div>
      }

      {activeTab === 'stories' &&
      <div className="space-y-12">
          <SuccessStories stories={filteredStories} />
        </div>
      }

      {/* Instagram Feed Section */}
      <div className="mt-16">
        <InstagramFeed posts={instagramPosts} />
      </div>

      {/* Photo Submission Section */}
      <div className="mt-16">
        <PhotoSubmission onSubmit={handlePhotoSubmit} />
      </div>
    </div>);

};

export default GalleryInteractive;